import { useEffect, useState } from 'react';

const STAGING_TOKEN = import.meta.env.VITE_STAGING_TOKEN as string | undefined;
const SESSION_KEY = 'staging_access';

/**
 * StagingGate — protects the staging environment via a secret URL token.
 *
 * Usage: share staging.app.qinv.ai?token=<VITE_STAGING_TOKEN>
 * - On first visit, token is validated and saved to sessionStorage
 * - Token is removed from the URL after validation (clean URL)
 * - Subsequent navigation within the session works without the token
 * - Only active when VITE_STAGING_TOKEN is set — transparent in production
 */
export function StagingGate({ children }: { children: React.ReactNode }) {
  // Initialize synchronously to avoid flash of "Acesso não autorizado" on hard navigations
  // (e.g. iOS Safari uses window.location.href which remounts the full React tree)
  const [allowed, setAllowed] = useState(() => {
    if (!STAGING_TOKEN) return true;
    try {
      return sessionStorage.getItem(SESSION_KEY) === STAGING_TOKEN;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!STAGING_TOKEN || allowed) return;

    // Check token in URL (first visit with token)
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token === STAGING_TOKEN) {
      sessionStorage.setItem(SESSION_KEY, STAGING_TOKEN);

      // Remove token from URL to keep it clean
      params.delete('token');
      const clean = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', clean);

      setAllowed(true);
    }
  }, [allowed]);

  if (!allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-text-secondary text-sm">Acesso não autorizado.</p>
      </div>
    );
  }

  return <>{children}</>;
}
